import { useCallback, useState } from 'react';
import { useGetPatientSessionsExportQuery } from '../../../api';
import { Button } from '../../../components/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/Dialog';
import { DateRangePickerInput } from '../../../components/Input/DateRangePickerInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/Input/Select';
import { useDateRange } from '../../../hooks/useDateRange';
import { useToaster } from '../../../hooks/useToaster';

interface ExportPatientSessionsModalProps {
  patientId: number;
  initialDateRange?: [Date, Date] | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ExportPatientSessionsModal = ({
  patientId,
  initialDateRange,
  open,
  onOpenChange
}: ExportPatientSessionsModalProps) => {
  const toaster = useToaster();
  const [exportSessions, { isLoading }] = useGetPatientSessionsExportQuery();
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(initialDateRange ?? null);
  const { dateFrom, dateTo } = useDateRange(dateRange);
  const [format, setFormat] = useState('xlsx');

  const onDownload = useCallback(async () => {
    try {
      await exportSessions({
        patientId,
        dateFrom,
        dateTo,
        format
      }).unwrap();
      onOpenChange(false);
      toaster.success('Your data export is ready and will download shortly.');
    } catch {
      toaster.error('Failed to export the data. Please try again.');
    }
  }, [patientId, dateFrom, dateTo, format, exportSessions, onOpenChange, toaster]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="export-sessions-modal">
        <DialogHeader>
          <DialogTitle>Export Patient Sessions</DialogTitle>
        </DialogHeader>

        <p>Please confirm the date range from which to export:</p>

        <div className="md:max-w-[380px]">
          <DateRangePickerInput
            value={dateRange}
            onChange={setDateRange}
            testId="date-range-input"
            className="w-full"
          />
        </div>

        <p>Please choose a target file format:</p>

        <div>
          <Select value={format} onValueChange={(value) => setFormat(value)}>
            <SelectTrigger className="h-[42px] w-full md:w-[380px]" autoFocus>
              <SelectValue placeholder="Select a file type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">CSV</SelectItem>
              <SelectItem value="xlsx">XLSX</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            variant="accent"
            onClick={onDownload}
            isLoading={isLoading}
            disabled={isLoading}
            data-testid="export-sessions-download-button"
          >
            Download
          </Button>

          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
